/*
    Компонент демо-формы.

    Работает как с виджетом подсказок, так и с классической отправкой формы.
    По умолчанию рендерит форму (labeled input с подсказками или без) + кнопка (если без подсказок),
    но вместо формы может выводиться другой контент (например, для гранулярных подсказок).
    Необязательно может рендерить (контент передается в $slots):
        - заголовок (slot = header)
        - кнопки переключения типа формы и тогглеры (slot = togglers)
        - дополнительный контент перед формой (например, уточняющие чекбоксы) (slot = formDetails)
        - ссылки после формы (slot = links)

    При отправке генерирует событие "form-submit".
    При работе через подсказки - прокидывает обработчики.

    Отправляет цели в аналитику - NotBounce при вводе текста
    и {pathname}_DEMO_FORM при отправке формы или выборе подсказки.

    При вводе текста в форму, скрывает результаты и ошибки.
    Обязательный проп - loading. Нужен, чтобы показывать результаты
    после ручного скрытия при вводе текста

    Обрабатывает состояние обновления результатов -
    opacity:0 во время обновления и opacity:1 по готовности результатов с transition

*/

import Analytics from "../../marketing/analytics";
import labeledInput from "../labeled-input";
import baseLoader from "../base-loader";

const template = /* html */`
    <section class="demo-form-module">
        <div class="storey color-white">
            <div class="mb-1 mb-sm-2">
                <template v-if="$slots.header">
                    <component :is="titleTag" class="module__title">
                        <slot name="header" />
                    </component>
                </template>
                <template v-if="$slots.togglers">
                    <div class="mt-1 mt-sm-0_25">
                        <slot name="togglers" />
                    </div>
                </template>
            </div>
            <transition name="slide-in">
                <template v-if="$slots.form_details">
                    <div class="mb-1">
                        <slot name="form_details" />
                    </div>
                </template>
            </transition>
            <slot name="form" :success="handleChildSubmit">
                <form
                    class="flex gap-0_5 wrap"
                    @submit.prevent="handleSubmit"
                    data-test="demo-form"
                >
                    <div
                        class="nx-demo-input-wrap flex-column"
                        ref="inputRef"
                    >
                        <labeled-input
                            class="flex-auto nx-demo-input"
                            :is-focused="inputFocused"
                            :name="inputName"
                            :label="inputLabel"
                            :input-type="inputType"
                            :value="modelValue"
                            :suggest="suggest"
                            :suggest-callbacks="{...suggestCallbacks, onSelect}"
                            :suggest-options="suggestOptions"
                            :clear-suggestions="clearSuggestions"
                            @input="handleInput"
                            data-test="demo-input"
                            notBounce
                            :autocomplete="autocomplete"
                        />
                    </div>
                    <div
                        class="demo-input-details basis-100"
                        v-if="$slots.inputDetails"
                    >
                        <slot name="inputDetails" />
                    </div>
                    <button
                        v-if="!suggest"
                        class="button-clean button-clean--medium
                               nx-demo-button basis-100 basis-sm-auto"
                        :disabled="!modelValue || loading || btnDisabled"
                        data-test="submit-btn"
                    >
                        [[ buttonText ]]
                    </button>
                </form>
            </slot>
            <transition name="fade">
                <div
                    :class="$slots.inputDetails ? 'mt-1' : 'mt-0_5'"
                    v-show="(loading && showLoader) || (error || $slots.results && !hideResults)"
                >
                    <base-loader
                        v-if="loading && showLoader"
                        class="hide"
                    />
                    <div v-show="!loading">
                        <transition-group name="fade">
                            <div
                                class="nx-error mt-1 hide"
                                v-if="error && !hideResults"
                                data-test="demo-error"
                            >
                                [[ error ]]
                            </div>
                            <div
                                :class="['nx-results hide', resultsAdaptivePadding && 'adaptive']"
                                v-if="$slots.results && !hideResults"
                                data-test="demo-results"
                            >
                                <div
                                    :class="[
                                        'nx-results-content',
                                        resultIsRefreshing ? 'transparent' : '']"
                                >
                                    <div
                                        v-if="$slots.partial_error"
                                        class="nx-results-partial-error mb-1_5 mb-sm-0"
                                        data-test="demo-partial-error"
                                    >
                                        <slot name="partial_error" />
                                    </div>
                                    <slot name="results" />
                                </div>
                            </div>
                        </transition-group>
                    </div>
                </div>
            </transition>
            <template v-if="$slots.links">
                <div class="mt-1 nx-links">
                    <slot name="links" />
                </div>
            </template>
            <slot />
        </div>
    </section>
`;

const props = {
    modelValue: {
        type: [String, Number],
        default: "",
    },
    inputName: {
        type: String,
        default: "demo-input",
    },
    inputType: {
        type: String,
        default: "text",
    },
    inputLabel: {
        type: String,
        required: true,
    },
    inputFocused: {
        type: Boolean,
        default: false,
    },
    buttonText: {
        type: String,
        default: "Отправить",
    },
    suggest: {
        type: String,
        default: "",
    },
    suggestCallbacks: {
        type: Object,
        default: () => {},
    },
    suggestOptions: {
        type: Object,
        default: () => {},
    },
    clearSuggestions: {
        type: Boolean,
        default: false,
    },
    loading: {
        type: Boolean,
        required: true,
    },
    error: {
        type: String,
        default: "",
    },
    goalPostfix: {
        type: String,
        default: "",
    },
    resultIsRefreshing: {
        type: Boolean,
        default: false,
    },
    autocomplete: {
        type: String,
        default: "",
    },
    titleTag: {
        type: String,
        default: "h2",
    },
    btnDisabled: {
        type: Boolean,
        default: false,
    },
    resultsAdaptivePadding: {
        type: Boolean,
        default: false,
    },
};

export default {
    template,
    data() {
        return {
            hideResults: false,
            timer: null,
            showLoader: false,
            baseGoalName: `${window.location.pathname
                .split("/")
                .filter(Boolean)
                .pop()
                .replaceAll("-", "_")
                .toUpperCase()
            }_DEMO_FORM`,
        };
    },
    props,
    watch: {
        loading(newVal, oldVal) {
            clearTimeout(this.timer);
            if (newVal) {
                this.timer = setTimeout(() => { this.showLoader = true; }, 150);
            } else {
                this.showLoader = false;
            }
            if (!oldVal || oldVal === newVal) return;
            this.hideResults = false;
        },
    },
    computed: {
        goalName() {
            return this.goalPostfix
                ? `${this.baseGoalName}_${this.goalPostfix}`
                : this.baseGoalName;
        },
    },
    methods: {
        onSelect(...args) {
            this.hideResults = false;
            Analytics.reachGoal(this.goalName);
            if (this.suggestCallbacks?.onSelect) {
                this.suggestCallbacks.onSelect(...args);
            }
        },
        handleSubmit() {
            this.hideResults = false;
            Analytics.reachGoal(this.goalName);
            this.$emit("form-submit");
        },
        handleInput(e) {
            if (!this.suggest) {
                this.hideResults = true;
            }
            this.$emit("update:modelValue", e.target.value);
        },
        handleChildSubmit(goalPostfix) {
            const goalName = goalPostfix
                ? `${this.baseGoalName}_${goalPostfix}`
                : this.baseGoalName;
            Analytics.reachGoal(goalName);
        },
    },
    emits: ["form-submit", "update:modelValue"],
    name: "DemoForm",
    components: {
        labeledInput,
        baseLoader,
    },
};
